<!-- https://phattranminh96.gitbook.io/vue-form-builder/ -->
<template>
    <div class="forms-page create-form-page">
        <!-- Modern Header -->
        <div class="forms-header">
            <div class="header-left">
                <h1 class="page-title">Create New Form</h1>
                <p class="page-subtitle">Design and configure a new patient form</p>
            </div>
            <div class="header-actions">
                <button v-if="aiFormsEnabled" type="button" class="btn-ai-edit" @click="openAiEdit" :disabled="loading || !formData">
                    <i class="fa fa-magic"></i> Edit with AI
                </button>
                <button v-else-if="aiFormsComingSoon" data-test="ai-forms-edit-coming-soon" type="button" class="btn-ai-edit" title="Edit with AI is coming soon" disabled>
                    <i class="fa fa-magic"></i> Edit with AI <span class="badge badge-light ml-1">Coming soon</span>
                </button>
                <button type="button" class="btn-save" @click="_saveForm" :disabled="loading">
                    <i class="fa fa-save"></i> Save Form
                </button>
            </div>
        </div>

        <ai-edit-form-modal v-if="aiFormsEnabled" ref="aiEditModal" :site-id="siteID" :config="formData" @apply="onAiEditApply" />

        <ErrorDisplay/>
        <CommonOverlay :show="loading" :title="overlayTitle" :message="overlayMessage" />

        <ai-setup-summary :report="aiSetupReport" :notes="aiSetupNotes" />

        <!-- Form Configuration Card -->
        <div class="config-card">
            <div class="config-card-header">
                <h3 class="config-title">
                    <i class="fa fa-cog"></i>
                    Form Configuration
                </h3>
            </div>
            <div class="config-card-body">
                <!-- Template Selection Row -->
                <div class="config-row template-row">
                    <div class="config-field template-field">
                        <label class="field-label">Load from Template</label>
                        <div class="template-selector">
                            <multiselect
                                v-model="selectedForm"
                                :options="forms"
                                :multiple="false"
                                :custom-label="formsCustomLabel"
                                track-by="Name"
                                placeholder="Select an existing form as template..."
                                class="modern-select"
                            >
                                <template slot="option" slot-scope="props">
                                    <div class="option-item">
                                        <i class="fa fa-file-text-o"></i>
                                        <span>{{ formsCustomLabel(props.option) }}</span>
                                    </div>
                                </template>
                            </multiselect>
                            <button type="button" class="btn-load-template" @click="loadForm" :disabled="!selectedForm || selectedForm.length === 0">
                                <i class="fa fa-download"></i> Load
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Form Details Row -->
                <div class="config-row details-row">
                    <div class="config-field">
                        <label class="field-label">Form Name <span class="required">*</span></label>
                        <input
                            v-model="formName"
                            type="text"
                            class="modern-input"
                            placeholder="Enter form name..."
                        />
                    </div>
                    <div class="config-field">
                        <label class="field-label">Form Type <span class="required">*</span></label>
                        <multiselect
                            v-model="selectedFormType"
                            :options="formTypes"
                            :multiple="false"
                            track-by="Name"
                            placeholder="Select form type..."
                            class="modern-select"
                        >
                        </multiselect>
                    </div>
                </div>

                <!-- Skip Validation Option -->
                <div class="config-row">
                    <div class="skip-validation-option">
                        <label class="skip-validation-toggle">
                            <input
                                type="checkbox"
                                v-model="skipValidation"
                            />
                            <span class="toggle-slider"></span>
                            <span class="toggle-label">Skip patient verification (DOB & Initials)</span>
                        </label>
                        <p class="skip-validation-hint">
                            When enabled, patients will be taken directly to the form without verifying their Date of Birth and Initials.
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Form Builder Section -->
        <div class="builder-section">
            <FormBuilder v-model="formData" :siteID="siteID" :formType="selectedFormTypeValue"></FormBuilder>
        </div>
    </div>
</template>

<script>
    /**
     * NOTE - This file only exists for development purpose.
     */
    import FormBuilder from "./FormBuilder";
    import FormRenderer from "./FormRenderer";    
    import { mapGetters, mapState, mapActions, mapMutations } from 'vuex';
    import ErrorDisplay from "@/components/ErrorDisplay/ErrorDisplay";
    import CommonOverlay from "@/components/CommonOverlay";
    import Multiselect from "vue-multiselect";
    import mixin from '../../../mixin.js';
    import moment from 'moment-timezone';
    import { langSuffix, LANGUAGE_CONFIG } from '../utils/languageHelper';
    import { sanitizeFormConfig } from '../libraries/sanitize-config';
    import { getMissingOdImagingFields } from '../libraries/validate-od-imaging-config';
    import { getFormOdUniqueMappingError } from '../libraries/validate-od-unique-mapping';
    import { getFormOdCategorizedOtherProblemsError } from '../libraries/od-categorized-diseases';
    import { EVENT_CONSTANTS } from '../configs/events';
    import { CONTROLS } from '../configs/controls';
    import AiEditFormModal from './AiEditFormModal.vue';
    import AiSetupSummary from './AiSetupSummary.vue';

    export default {
        name: "CreateForm",
        props: ['formID'],
        components: {
            FormBuilder,
            FormRenderer,
            ErrorDisplay,
            Multiselect,
            AiEditFormModal,
            AiSetupSummary,
            CommonOverlay
        },
        mixins: [mixin],
        created() {
            this.init();
            this.$formEvent.$on("submit", async(value) => {
                //console.dir(this.formInputData);
                if(!this.selectedForm || !this.selectedForm.FormIDForWeb) {                    
                    this.$toast.error('Somethng is not right. Please try refreshing and submitting the form again');
                    return;
                }
                await this._saveFormSubmission();
            })
        },

        data: () => ({            
            forms: [],
            formCategories: [],
            formOptions: [],            
            selectedForm: [],
            selectedFormType: [],
            formTypes: ['NP_FORM', 'MED_HX_UPDATE', 'OTHER'],
            formIDForWeb: null,
            formName: "",
            formData: null,
            skipValidation: false,  // Skip DOB & Initials verification - default false
            isShowDevNote: false,
            isRenderer: false,
            formInputData: null,
            isShowData: false,
            readOnly: false,
            aiSetupReport: null,   // AI "what I configured / what needs attention" summary
            aiSetupNotes: [],      // AI generation notes (auto-adjustments)
        }),

        methods: {
            ...mapActions('alert', ['setDismissSuccessCountDown', 'setErrored', 'setMessage']),
            ...mapActions('forms', ['getForms', 'saveForm', 'saveFormSubmission', 'translateFormConfig', 'editForm']),

            openAiEdit() {
                this.$refs.aiEditModal.show();
            },

            // Apply an AI edit (ids + conditional-logic graph already preserved by the
            // backend); run through sanitizeFormConfig for shape consistency, then swap in.
            // The setup report (what was configured / what still needs attention) refreshes
            // the summary panel so the staff member sees the result of this edit too.
            onAiEditApply(editedConfig, setupReport) {
                const { sanitized } = sanitizeFormConfig(editedConfig, { controlDefinitions: CONTROLS });
                this.formData = sanitized;
                if (setupReport) this.aiSetupReport = setupReport;
            },

            async init() {
                await this._getForms();
                this._applyAiDraftIfPresent();
            },

            /**
             * "Create with AI" hand-off. If AiGenerateFormModal stashed a generated
             * draft in the store, load it into the builder UNSAVED (mirrors loadForm),
             * prefill name/type, surface any warnings, then clear the transient slot.
             * Runs after _getForms so FormBuilder has mounted and pdfCategories/logoUrl
             * are available to graft onto the draft.
             */
            _applyAiDraftIfPresent() {
                const draft = this.$store.state.forms.aiDraft;
                if (!draft || !draft.configData) return;

                const { sanitized } = sanitizeFormConfig(draft.configData, { controlDefinitions: CONTROLS });

                // Preserve the pdfCategories / logoUrl that _getForms attached to the
                // default formData (the draft replaces formData wholesale, like loadForm).
                if (sanitized.formConfig) {
                    sanitized.formConfig.pdfCategories = this.formCategories;
                    const site = this.getSite(this.siteID);
                    if (site && site.LogoUrl) sanitized.formConfig.logoUrl = site.LogoUrl;
                }

                this.formData = sanitized;
                this.formName = draft.suggestedName || '';
                this.selectedFormType = draft.suggestedFormType || 'OTHER';

                // Surface the AI setup summary in a persistent panel (what was configured +
                // what the staff member still needs to fix) instead of a transient toast.
                this.aiSetupReport = draft.setupReport || null;
                this.aiSetupNotes = Array.isArray(draft.warnings) ? draft.warnings : [];

                this.$store.commit('forms/clearAiDraft');
            },

            formsCustomLabel({Name, FormIDForWeb}) {
                return `${Name}`;
            },

            getData() {
                console.log(JSON.stringify(this.formData))
            },

            createBlank() {
                this.formData = Object.assign({})
            },

            setData() {
                //this.formData = Object.assign({}, DEMO_FORM_DATA);
            },

            setDemoData() {
                //this.formData = Object.assign({}, DEMO_FORM_DATA);
            },

            viewDemoForm() {
                if (!this.isRenderer) {
                    this.setDemoData();
                    this.isRenderer = true;
                    return;
                }
                this.isRenderer = false;
            },

            viewRenderer() {
                if (!this.isRenderer) {
                    this.setData();
                    this.isRenderer = true;
                    return;
                }

                this.isRenderer = false;
            },

            setReadonly() {
                if (this.readOnly) {
                    this.readOnly = false;
                    return;
                }

                this.setRandomData();
                this.readOnly = true;
            },

            async _getForms() {
                let res = await this.getForms({siteID: this.siteID});
                //console.log(res);
                this.forms = res.forms;
                this.formCategories = res.categories;

                this.formData.formConfig.pdfCategories = this.formCategories;

                let site = this.getSite(this.siteID);
                
                if(site.LogoUrl)
                    this.formData.formConfig.logoUrl = site.LogoUrl;

                // add label and track-by property because it's required by multiselect
                //res.data.map(form => ({ ...form, label: form.Name, `track-by`: form.FormIDForWeb }));                
            },

            loadForm() {                
                this.formData = JSON.parse(this.selectedForm.ConfigData);         
                this.formData.formConfig.pdfCategories = this.formCategories;       
            },

            async _saveForm() {
                if(this.formName === "") {
                    this.$toast.error(`Please enter a name for this form before saving`);
                    return false;
                }

                // Covers every "no type picked" shape the multiselect can produce ([], null, '').
                // A bare .length here threw a TypeError on null, which killed the save silently
                // instead of showing this toast.
                if(!this.selectedFormTypeValue) {
                    this.$toast.error(`Please select the type of the form you're trying to create before saving.`);
                    return false;
                }

                const missingOdImagingFields = getMissingOdImagingFields((this.formData && this.formData.formConfig) || {});
                if (missingOdImagingFields.length > 0) {
                    this.$toast.error(`Cannot save form: please set ${missingOdImagingFields.join(' and ')} in Form Configurations. Forms without these settings will not save to Open Dental when patients submit them.`, { timeout: 9000 });
                    this.$formEvent.$emit(EVENT_CONSTANTS.BUILDER.SIDEBAR.OPEN, 'FormConfiguration');
                    return false;
                }

                // Two controls on one Open Dental list column join with '\n' at submit time and
                // wipe the patient's problems / allergies / medications. Refuse the save.
                const duplicateOdMappingError = getFormOdUniqueMappingError(this.formData || {});
                if (duplicateOdMappingError) {
                    this.$toast.error(duplicateOdMappingError, { timeout: 12000 });
                    return false;
                }

                // A categorized diseases field saved to Diseases writes Other Problems itself; a
                // second writer would be concatenated into the same note. Refuse the save.
                const otherProblemsError = getFormOdCategorizedOtherProblemsError(this.formData || {});
                if (otherProblemsError) {
                    this.$toast.error(otherProblemsError, { timeout: 12000 });
                    return false;
                }

                // Auto-translate form controls for all enabled languages
                // Note: Translation only works for existing forms (formIDForWeb required)
                // New forms will be translated on subsequent saves
                const targetLanguages = this.enabledTranslationLanguages;
                if (targetLanguages.length > 0 && this.formIDForWeb) {
                    for (const lang of targetLanguages) {
                        try {
                            console.log(`Translating form to ${lang.name} (${lang.code})...`);
                            this.$store.dispatch('alert/setLoading', true);

                            const translateResponse = await this.translateFormConfig({
                                formIDForWeb: this.formIDForWeb,
                                targetLanguage: lang.code,
                                forceRetranslate: false
                            });

                            if (translateResponse && translateResponse.data && translateResponse.data.translatedConfigData) {
                                const translatedData = translateResponse.data.translatedConfigData;
                                const suffix = langSuffix(lang.code);

                                // Merge only translation fields (preserve user's current edits)
                                if (translatedData.controls && this.formData.controls) {
                                    for (const [controlId, translatedControl] of Object.entries(translatedData.controls)) {
                                        if (this.formData.controls[controlId]) {
                                            for (const [key, value] of Object.entries(translatedControl)) {
                                                if (key.endsWith(suffix)) {
                                                    this.formData.controls[controlId][key] = value;
                                                }
                                            }
                                        }
                                    }
                                }
                                if (translatedData.sections && this.formData.sections) {
                                    for (const [sectionId, translatedSection] of Object.entries(translatedData.sections)) {
                                        if (this.formData.sections[sectionId]) {
                                            for (const [key, value] of Object.entries(translatedSection)) {
                                                if (key.endsWith(suffix)) {
                                                    this.formData.sections[sectionId][key] = value;
                                                }
                                            }
                                        }
                                    }
                                }
                                if (translatedData.formConfig && this.formData.formConfig) {
                                    for (const [key, value] of Object.entries(translatedData.formConfig)) {
                                        if (key.endsWith(suffix)) {
                                            this.formData.formConfig[key] = value;
                                        }
                                    }
                                }
                                console.log(`Auto-translation to ${lang.name} completed. Stats:`, translateResponse.data.stats);
                            }
                        } catch (translateError) {
                            console.warn(`Auto-translation to ${lang.name} failed, continuing:`, translateError);
                        }
                    }
                } else if (targetLanguages.length > 0 && !this.formIDForWeb) {
                    console.log('Translation languages enabled but form not yet saved. Translation will occur on next save.');
                }

                let payload = {
                    siteID: this.siteID,
                    formIDForWeb: this.formIDForWeb,
                    name: this.formName,
                    formType: this.selectedFormTypeValue,
                    formData: JSON.stringify(this.formData),
                    skipValidation: this.skipValidation,  // Include skip validation setting
                    _date: moment().format('YYYY-MM-DD HH:mm:ss')
                };
                //console.dir(payload);
                try {
                    this.$store.dispatch('alert/setLoading', true);
                    let res = await this.saveForm(payload);
                    this.$toast.success('Form saved successfully!');
                    this.formIDForWeb = res.data;
                } catch (error) {
                    const serverMsg = error && error.response && typeof error.response.data === 'string' && error.response.data;
                    this.$toast.error(serverMsg || error.message);
                } finally {
                    this.$store.dispatch('alert/setLoading', false);
                }
            },
        },

        computed: {
            ...mapGetters({
                getSite: "customer/getSite"
            }),
            ...mapState({
                loading: state => state.alert.loading
            }),
            /**
             * The form type as a plain string, for passing down to the builder.
             *
             * selectedFormType is bound to a multiselect and holds three shapes over its life:
             * [] before anything is picked, the option string once picked, and null when an
             * already-selected option is clicked again. Only a non-empty string is a real type;
             * everything else normalizes to null so the builder treats the type as unknown.
             */
            selectedFormTypeValue() {
                return typeof this.selectedFormType === 'string' && this.selectedFormType !== ''
                    ? this.selectedFormType
                    : null;
            },
            /**
             * Get list of enabled translation languages (non-English) for this form
             */
            enabledTranslationLanguages() {
                if (!this.formData || !this.formData.formConfig) return [];
                const supported = this.formData.formConfig.supportedLanguages || [];
                return supported
                    .filter(code => code !== 'en' && LANGUAGE_CONFIG[code])
                    .map(code => ({ code, name: LANGUAGE_CONFIG[code].name }));
            },
            overlayTitle() {
                // If forms data not yet initialized, show loading; otherwise show saving when loading
                if (!this.forms || this.forms.length === 0) return 'Loading forms';
                return this.loading ? 'Saving form' : '';
            },
            overlayMessage() {
                if (!this.forms || this.forms.length === 0) return 'Please wait while we load available templates...';
                return this.loading ? 'Please wait while we save the form...' : '';
            }
        }
    }
</script>

<!-- New step!
     Add Multiselect CSS. Can be added as a static asset or inside a component. -->
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss" scoped>
// Modern Forms Page Styling - matching Admin Dashboard
.forms-page.create-form-page {
    font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;
    color: #334155;
    background-color: #f8fafc;
    padding: 1.5rem 2rem;
    min-height: 100vh;

    @media (max-width: 768px) {
        padding: 1rem;
    }
}

// Header
.forms-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 24px;
    flex-wrap: wrap;
    gap: 16px;

    .header-left {
        flex: 1;
    }

    .header-actions {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;
    }
}

.page-title {
    font-size: 1.75rem;
    font-weight: 600;
    color: #0f172a;
    margin: 0 0 4px 0;
    line-height: 1.3;
}

.page-subtitle {
    font-size: 0.9375rem;
    color: #64748b;
    margin: 0;
}

// Edit with AI Button
.btn-ai-edit {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background-color: #6366f1;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-right: 12px;

    &:hover:not(:disabled) { background-color: darken(#6366f1, 8%); transform: translateY(-1px); }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
}

// Save Button
.btn-save {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background-color: #3abf94;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background-color: darken(#3abf94, 8%);
        transform: translateY(-1px);
    }

    &:active {
        transform: translateY(0);
    }

    i {
        font-size: 0.875rem;
    }
}

// Configuration Card
.config-card {
    background-color: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    border: 1px solid #e2e8f0;
    margin-bottom: 24px;
    overflow: hidden;
}

.config-card-header {
    padding: 16px 20px;
    background-color: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
}

.config-title {
    font-size: 1rem;
    font-weight: 600;
    color: #0f172a;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 10px;

    i {
        color: #64748b;
        font-size: 1rem;
    }
}

.config-card-body {
    padding: 20px;
}

.config-row {
    margin-bottom: 20px;

    &:last-child {
        margin-bottom: 0;
    }
}

.details-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
}

.template-row {
    .template-field {
        max-width: 100%;
    }
}

.config-field {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.field-label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #475569;
    margin: 0;

    .required {
        color: #ef4444;
    }
}

.modern-input {
    padding: 10px 14px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 0.875rem;
    color: #334155;
    background-color: white;
    transition: all 0.2s ease;

    &::placeholder {
        color: #94a3b8;
    }

    &:focus {
        outline: none;
        border-color: #3abf94;
        box-shadow: 0 0 0 3px rgba(58, 191, 148, 0.1);
    }
}

// Template Selector
.template-selector {
    display: flex;
    gap: 12px;
    align-items: flex-start;

    .modern-select {
        flex: 1;
    }
}

.btn-load-template {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 10px 16px;
    background-color: #f1f5f9;
    color: #475569;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover:not(:disabled) {
        background-color: #e2e8f0;
        border-color: #cbd5e1;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    i {
        font-size: 0.8125rem;
    }
}

// Modern Multiselect Styling
::v-deep .modern-select {
    .multiselect__tags {
        min-height: 42px;
        padding: 8px 40px 0 12px;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        background-color: white;
        transition: all 0.2s ease;
    }

    .multiselect__tags:focus-within {
        border-color: #3abf94;
        box-shadow: 0 0 0 3px rgba(58, 191, 148, 0.1);
    }

    .multiselect__single {
        font-size: 0.875rem;
        color: #334155;
        margin-bottom: 6px;
        padding-left: 0;
    }

    .multiselect__placeholder {
        font-size: 0.875rem;
        color: #94a3b8;
        margin-bottom: 6px;
        padding-left: 0;
        padding-top: 0;
    }

    .multiselect__input {
        font-size: 0.875rem;
        margin-bottom: 6px;
        padding-left: 0;
    }

    .multiselect__content-wrapper {
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        margin-top: 4px;
    }

    .multiselect__option {
        padding: 10px 14px;
        font-size: 0.875rem;
        color: #334155;

        &--highlight {
            background-color: #f1f5f9;
            color: #334155;
        }

        &--selected {
            background-color: rgba(58, 191, 148, 0.1);
            color: #3abf94;
            font-weight: 500;
        }
    }

    .option-item {
        display: flex;
        align-items: center;
        gap: 10px;

        i {
            color: #64748b;
            font-size: 0.875rem;
        }
    }
}

// Skip Validation Option
.skip-validation-option {
    background-color: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 16px;
}

.skip-validation-toggle {
    display: flex;
    align-items: center;
    cursor: pointer;
    margin-bottom: 0;
    gap: 12px;

    input[type="checkbox"] {
        width: 18px;
        height: 18px;
        cursor: pointer;
        accent-color: #3abf94;
        border-radius: 4px;
    }

    .toggle-label {
        font-weight: 600;
        font-size: 0.875rem;
        color: #334155;
    }
}

.skip-validation-hint {
    margin: 10px 0 0 30px;
    font-size: 0.8125rem;
    color: #64748b;
    line-height: 1.5;
}

// Builder Section
.builder-section {
    background-color: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    border: 1px solid #e2e8f0;
    overflow: hidden;
}
</style>
